// Lumynest product-page offers: sale price display + buy-one-for-a-friend selector.
// Display only. The prices charged come from the automatic discounts in Shopify admin.
import { StandardEvents } from '@shopify/events';

const nowSeconds = () => Math.floor(Date.now() / 1000);

/**
 * Hides sale-only markup once the end time has passed (covers cached HTML).
 * @param {HTMLElement} el
 */
function applyExpiry(el) {
  if (el.dataset.saleActive !== 'true') return;
  const ends = Number(el.dataset.ends) || 0;
  if (ends > nowSeconds()) return;
  el.dataset.saleActive = 'false';
  el.querySelectorAll('[data-lum-sale]').forEach((node) => {
    node.hidden = true;
  });
  el.querySelectorAll('[data-lum-regular]').forEach((node) => {
    node.hidden = false;
  });
}

/**
 * Waits for Horizon's variant-change section render and hands back this block's fresh markup.
 * @param {HTMLElement} el
 * @param {any} event
 * @param {(fresh: HTMLElement) => void} update
 */
function onVariantHtml(el, event, update) {
  if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
  if (!event.promise) return;
  event.promise
    .then((result) => {
      const html = result?.detail?.html;
      if (!html) return;
      const fresh = html.querySelector(`${el.localName}[data-block-id="${el.dataset.blockId}"]`);
      if (fresh) update(fresh);
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[lumynest-offers]', error);
    });
}

class LumynestPrice extends HTMLElement {
  connectedCallback() {
    this.scope = this.closest('.shopify-section, dialog');
    this.handleSelect = (event) =>
      onVariantHtml(this, event, (fresh) => {
        this.innerHTML = fresh.innerHTML;
        this.dataset.saleActive = fresh.dataset.saleActive || 'false';
        this.dataset.ends = fresh.dataset.ends || '0';
        applyExpiry(this);
      });
    this.scope?.addEventListener(StandardEvents.productSelect, this.handleSelect);
    applyExpiry(this);
  }

  disconnectedCallback() {
    this.scope?.removeEventListener(StandardEvents.productSelect, this.handleSelect);
  }
}

class LumynestBundle extends HTMLElement {
  connectedCallback() {
    this.scope = this.closest('.shopify-section, dialog');

    this.handleChange = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.matches('[data-lum-qty]') && target.checked) {
        this.setQuantity(Number(target.dataset.lumQty) || 1);
      }
      this.updateSurprise();
    };

    this.handleQty = (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest('product-form-component')) return;
      this.syncRadios(Number(event.detail?.quantity) || 1);
    };

    this.handleSelect = (event) =>
      onVariantHtml(this, event, (fresh) => {
        fresh.querySelectorAll('[data-lum-slot]').forEach((slot) => {
          const current = this.querySelector(`[data-lum-slot="${slot.getAttribute('data-lum-slot')}"]`);
          if (current) current.innerHTML = slot.innerHTML;
        });
        this.dataset.saleActive = fresh.dataset.saleActive || 'false';
        this.dataset.ends = fresh.dataset.ends || '0';
        applyExpiry(this);
      });

    this.addEventListener('change', this.handleChange);
    this.scope?.addEventListener('quantity-selector:update', this.handleQty);
    this.scope?.addEventListener(StandardEvents.productSelect, this.handleSelect);

    applyExpiry(this);
    this.syncRadios(Number(this.quantityInput?.value) || 1);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    this.scope?.removeEventListener('quantity-selector:update', this.handleQty);
    this.scope?.removeEventListener(StandardEvents.productSelect, this.handleSelect);
  }

  get quantitySelector() {
    const form = document.getElementById(this.dataset.formId || '');
    return form ? form.querySelector('quantity-selector-component') : null;
  }

  get quantityInput() {
    const selector = this.quantitySelector;
    return selector ? selector.querySelector('input[name="quantity"]') : null;
  }

  /** @param {number} qty */
  setQuantity(qty) {
    const selector = /** @type {any} */ (this.quantitySelector);
    const input = this.quantityInput;
    if (!input) return;
    input.value = String(qty);
    if (selector && typeof selector.updateButtonStates === 'function') selector.updateButtonStates();
  }

  /** @param {number} qty */
  syncRadios(qty) {
    const one = this.querySelector('[data-lum-qty="1"]');
    const two = this.querySelector('[data-lum-qty="2"]');
    if (one instanceof HTMLInputElement && two instanceof HTMLInputElement) {
      if (qty >= 2) two.checked = true;
      else one.checked = true;
    }
    this.updateSurprise();
  }

  updateSurprise() {
    const wrap = this.querySelector('[data-lum-surprise]');
    if (!(wrap instanceof HTMLElement)) return;
    const two = this.querySelector('[data-lum-qty="2"]');
    const isTwo = two instanceof HTMLInputElement && two.checked;
    wrap.hidden = !isTwo;
    const toggle = wrap.querySelector('[data-lum-surprise-toggle]');
    const on = isTwo && toggle instanceof HTMLInputElement && toggle.checked;
    const fields = wrap.querySelector('[data-lum-surprise-fields]');
    if (fields instanceof HTMLElement) fields.hidden = !on;
    wrap.querySelectorAll('[data-lum-gift-input]').forEach((input) => {
      if (input instanceof HTMLInputElement || input instanceof HTMLSelectElement) input.disabled = !on;
    });
  }
}

if (!customElements.get('lumynest-price')) customElements.define('lumynest-price', LumynestPrice);
if (!customElements.get('lumynest-bundle')) customElements.define('lumynest-bundle', LumynestBundle);
